var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StatusPermission } from '../../enum';
import { useUserPermissionsAPI } from '../app/selectors';
import { ConfirmDialog, NativeNumberInput } from '../common';
import { useEffectivePlanPermission, useIsPlanLocked } from '../wizard/selectors';
import { DailyCompareTable } from './dailyCompareTable';
import { DataPanelFilters } from './dataPanelFilters';
import { DataPanelMetricTabs } from './dataPanelMetricTabs';
import { DataPanelSummaryBoxes } from './dataPanelSummaryBoxes';
import { DataPanelSummaryChart } from './dataPanelSummaryChart';
import { FREQUENCY_ENUM } from './enum';
import { PeriodCompareTable } from './periodCompareTable';
import { createParamsFetchActual, useActualReportAPI, useDeleteManualInputDataAPI } from './selectors';
export var DataPanelLayout = styled(Box)(function (_a) {
    var theme = _a.theme;
    return ({
        padding: theme.spacing(8),
        border: '0.075rem solid',
        borderColor: theme.palette.divider,
        borderRadius: '0.3125rem',
        backgroundColor: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(6),
    });
});
export var StyledNativeNumberInput = styled(NativeNumberInput)(function (_a) {
    var theme = _a.theme, disabled = _a.disabled, hasError = _a.hasError;
    return (__assign(__assign({ minHeight: '2.3125rem', width: '100%', backgroundColor: theme.palette.common.white, border: '0.0625rem solid', borderColor: theme.palette.primary.main, '&:hover': {
            borderWidth: '0.125rem',
        }, '&:has(input:focus)': {
            borderColor: theme.palette.primary.main,
            borderWidth: '0.125rem',
            boxShadow: 'unset',
        }, '&:has(input::focus-within)': {
            boxShadow: 'unset',
        } }, (hasError && { borderColor: theme.palette.error.main, borderWidth: '0.125rem' })), (disabled && { border: 'none' })));
});
var StyledConfirmationText = styled(Typography)(function (_a) {
    var theme = _a.theme;
    return ({
        color: theme.palette.primary.main,
    });
});
var StyledAdditionalText = styled(Typography)(function (_a) {
    var theme = _a.theme;
    return ({
        color: theme.palette.primary.main,
        fontWeight: 'bold',
        marginTop: theme.spacing(4),
    });
});
export var DataPanel = function (_a) {
    var planType = _a.planType, sectionType = _a.sectionType, timelines = _a.timelines, requesting = _a.requesting;
    var _b = useState(false), open = _b[0], setOpen = _b[1];
    var actualPermission = useUserPermissionsAPI().actualPermission;
    var effectiveActualPermission = useEffectivePlanPermission(actualPermission);
    var isPlanLocked = useIsPlanLocked();
    var disabled = effectiveActualPermission !== StatusPermission.Edit || isPlanLocked;
    var _c = useFormContext(), watch = _c.watch, handleSubmit = _c.handleSubmit;
    var t = useTranslation('report', { keyPrefix: 'actual.dailyCompareTable' }).t;
    var _d = watch(), frequency = _d.frequency, planId = _d.planId, queueId = _d.queueId, queueLocationId = _d.queueLocationId;
    var _e = useDeleteManualInputDataAPI(), actualDataManualDeleted = _e[0], deleteActualManualInputData = _e[1];
    var _f = useActualReportAPI(), fetchActualReport = _f[1], clearActualReport = _f[2];
    var handleFetchActualReport = handleSubmit(function (formData) {
        clearActualReport();
        fetchActualReport({}, createParamsFetchActual(formData));
    });
    useEffect(function () {
        if (actualDataManualDeleted.data)
            void handleFetchActualReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actualDataManualDeleted.data]);
    var handleClearData = function () {
        setOpen(false);
        deleteActualManualInputData(undefined, queueLocationId === undefined || queueLocationId < 0 ? { planId: planId } : { planId: planId, queueId: queueId, queueLocationId: queueLocationId });
    };
    var handleOpenClearDialog = function () {
        setOpen(true);
    };
    return (_jsxs(DataPanelLayout, __assign({ component: "section", sx: { marginTop: 3 } }, { children: [_jsx(DataPanelMetricTabs, { sectionType: sectionType, frequency: frequency }), _jsx(DataPanelFilters, { timelines: timelines, planType: planType }), _jsx(DataPanelSummaryBoxes, {}), _jsx(DataPanelSummaryChart, {}), frequency === FREQUENCY_ENUM.PERIOD ? (_jsx(PeriodCompareTable, { disabled: disabled, clearDisabled: disabled, onOpenClearDialog: handleOpenClearDialog })) : (_jsx(DailyCompareTable, { requesting: requesting, disabled: disabled, clearDisabled: disabled, onOpenClearDialog: handleOpenClearDialog, onRefetchReport: handleFetchActualReport })), _jsx(ConfirmDialog, { open: open, onClose: function () { return setOpen(false); }, title: t('confirmDialog.title'), message: _jsxs(_Fragment, { children: [_jsx(StyledConfirmationText, { children: t('confirmDialog.message') }), _jsx(StyledAdditionalText, { children: t('confirmDialog.subMessage') })] }), onConfirm: handleClearData, confirmButtonText: "continue", errors: [actualDataManualDeleted.error] })] })));
};
